import { copy } from "shared/assets";
import { ISelectOption } from "shared/components/Form/Fields/select/types";
import { DateChangeProps, IFieldType, IFormFields } from "shared/types";
import { DateFormat, TimeFormat } from "shared/utils";

const errors = copy.errors;
const travelLegFields = copy.travelBlock.travelLeg.fields;
const travelLegMessages = copy.travelBlock.travelLeg.messages;
const flightFields = copy.travelBlock.flight.fields;

export interface TravelLegFieldsProps {
  suggestedLocations?: ISelectOption[];
  timeFormat: TimeFormat;
  dateFormat: DateFormat;
  handleStartDateChange: (date: DateChangeProps) => void;
  handleEndDateChange: (date: DateChangeProps) => void;
  isStartBeforeEnd: boolean;
  fetchGoogleLocations: (term: string) => void;
  isFetchingLocations?: boolean;
  resetLocations?: () => void;
  onSelectDeparture: (location: ISelectOption) => void;
  onSelectArrival: (location: ISelectOption) => void;
}

export const getPrefillFields = (): IFormFields => [
  {
    name: "prefillFlightNumber",
    label: flightFields.flightNumber,
    type: IFieldType.text,
    note: "eg. QQ1234",
    style: { gridColumn: "span 3" },
    validation: {
      required: errors.required,
      minLength: {
        value: 3,
        message: errors.notValid,
      },
    },
  },
  {
    name: "prefillFlightDate",
    label: flightFields.flightDate,
    type: IFieldType.datePicker,
    minDate: new Date(),
    style: { gridColumn: "span 3" },
    validation: {
      required: errors.required,
    },
  },
];

export const getFields = ({
  suggestedLocations = [],
  timeFormat,
  dateFormat,
  handleStartDateChange,
  handleEndDateChange,
  isStartBeforeEnd,
  fetchGoogleLocations,
  isFetchingLocations,
  resetLocations,
  onSelectDeparture,
  onSelectArrival,
}: TravelLegFieldsProps): IFormFields => [
  {
    name: "departure.location",
    label: travelLegFields.departure,
    placeholder: travelLegFields.chooseLocation,
    type: IFieldType.select,
    options: suggestedLocations,
    isLoading: isFetchingLocations,
    isSearchable: true,
    onInputChange: (newValue, actionMeta) => {
      if (actionMeta.action === "input-change") {
        fetchGoogleLocations(newValue);
      }
    },
    onDropdownClose: resetLocations,
    onChange: onSelectDeparture,
    fieldLink: {
      // hide reset link
      label: "",
      onClick: () => {},
    },
    validation: {
      required: errors.required,
    },
    style: {
      gridColumn: "span 3",
    },
  },
  {
    name: "arrival.location",
    label: travelLegFields.destination,
    placeholder: travelLegFields.chooseLocation,
    type: IFieldType.select,
    options: suggestedLocations,
    isLoading: isFetchingLocations,
    isSearchable: true,
    onInputChange: (newValue, actionMeta) => {
      if (actionMeta.action === "input-change") {
        fetchGoogleLocations(newValue);
      }
    },
    onDropdownClose: resetLocations,
    onChange: onSelectArrival,
    fieldLink: {
      // hide reset link
      label: "",
      onClick: () => {},
    },
    validation: {
      required: errors.required,
    },
    style: {
      gridColumn: "span 3",
    },
  },
  {
    name: "departure.date",
    label: travelLegFields.departureDate,
    type: IFieldType.datePicker,
    dateFormat,
    validation: {
      required: errors.required,
      validate: () => isStartBeforeEnd || travelLegMessages.startBeforeEnd,
    },
    onChange: (value: string) => handleStartDateChange({ date: value }),
    style: {
      gridColumn: "span 2",
      mobile: {
        gridColumn: "span 3",
      },
    },
  },
  {
    name: "departure.time",
    label: travelLegFields.departureTime,
    type: IFieldType.simpleTime,
    timeFormat,
    validation: {
      required: errors.required,
      validate: () => isStartBeforeEnd,
    },
    onChange: (value: string) => handleStartDateChange({ time: value }),
    style: {
      gridColumn: "span 2",
      mobile: {
        gridColumn: "span 3",
      },
    },
  },
  {
    name: "departure.timeZone",
    label: travelLegFields.departureTimeZone,
    type: IFieldType.timezone,
    validation: {
      required: errors.required,
      validate: () => isStartBeforeEnd,
    },
    onChange: (data?: { value: string }) =>
      data?.value && handleStartDateChange({ timeZone: data.value }),
    style: {
      gridColumn: "span 2",
      mobile: {
        gridColumn: "span 6",
      },
    },
  },
  {
    name: "arrival.date",
    label: travelLegFields.arrivalDate,
    type: IFieldType.datePicker,
    dateFormat,
    validation: {
      required: errors.required,
    },
    onChange: (value: string) => handleEndDateChange({ date: value }),
    style: {
      gridColumn: "span 2",
      mobile: {
        gridColumn: "span 3",
      },
    },
  },
  {
    name: "arrival.time",
    label: travelLegFields.arrivalTime,
    timeFormat,
    type: IFieldType.simpleTime,
    validation: {
      required: errors.required,
    },
    onChange: (value: string) => handleEndDateChange({ time: value }),
    style: {
      gridColumn: "span 2",
      mobile: {
        gridColumn: "span 3",
      },
    },
  },
  {
    name: "arrival.timeZone",
    label: travelLegFields.arrivalTimeZone,
    type: IFieldType.timezone,
    validation: {
      required: errors.required,
    },
    onChange: (data?: { value: string }) =>
      data?.value && handleEndDateChange({ timeZone: data.value }),
    style: {
      gridColumn: "span 2",
      mobile: {
        gridColumn: "span 6",
      },
    },
  },
  {
    name: "airline",
    label: flightFields.airline,
    type: IFieldType.text,
    style: {
      gridColumn: "span 3",
    },
  },
  {
    name: "flightNumber",
    label: flightFields.flightNumber,
    type: IFieldType.text,
    style: {
      gridColumn: "span 3",
    },
  },
  {
    name: "info",
    label: travelLegFields.info,
    type: IFieldType.rte,
    style: {
      gridColumn: "span 6",
    },
  },
];
